import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import AttendanceForm from '../components/AttendanceForm';
import GroupAttendanceForm from '../components/GroupAttendanceForm';
import StudentSearchSelect from '../components/StudentSearchSelect';
import './Attendances.css';

const Attendances = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [filterStudent, setFilterStudent] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  
  // Inicializar fechas: día 1 del mes actual y día actual
  const getDefaultDateFrom = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  };
  
  const getDefaultDateTo = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  const [filterDateFrom, setFilterDateFrom] = useState(getDefaultDateFrom());
  const [filterDateTo, setFilterDateTo] = useState(getDefaultDateTo());
  const [tempDateFrom, setTempDateFrom] = useState(getDefaultDateFrom());
  const [tempDateTo, setTempDateTo] = useState(getDefaultDateTo());

  // Obtener grupos para el filtro
  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return response.data;
  });

  // Obtener asistencias
  const { data: attendances, isLoading, error } = useQuery(
    ['attendances', filterStudent, filterGroup, filterDateFrom, filterDateTo],
    async () => {
      const params = {};
      if (filterStudent) params.student_id = filterStudent;
      if (filterGroup) params.group_id = filterGroup;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const response = await api.get('/attendances', { params });
      return response.data;
    },
    {
      onError: (err) => {
        console.error('Error al cargar asistencias:', err);
      }
    }
  );

  const createAttendanceMutation = useMutation(
    (data) => api.post('/attendances', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('attendances');
        toast.success('Asistencia marcada exitosamente!');
        setIsModalOpen(false);
        setEditingAttendance(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        toast.error(errorDetail || 'Error al marcar asistencia');
      },
    }
  );

  const updateAttendanceMutation = useMutation(
    ({ id, data }) => api.put(`/attendances/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('attendances');
        toast.success('Asistencia actualizada exitosamente!');
        setIsModalOpen(false);
        setEditingAttendance(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        toast.error(errorDetail || 'Error al actualizar asistencia');
      },
    }
  );

  const handleCreateClick = () => {
    setEditingAttendance(null);
    setIsModalOpen(true);
  };

  const handleGroupAttendanceClick = () => {
    setIsGroupModalOpen(true);
  };

  const handleEditClick = (attendance) => {
    setEditingAttendance(attendance);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingAttendance) {
      updateAttendanceMutation.mutate({ id: editingAttendance.id, data });
    } else {
      createAttendanceMutation.mutate(data);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'present': { label: 'Presente', class: 'badge-success', icon: '✅' },
      'absent': { label: 'Ausente', class: 'badge-danger', icon: '❌' },
      'late': { label: 'Tarde', class: 'badge-warning', icon: '⏰' },
      'excused': { label: 'Justificado', class: 'badge-info', icon: '📝' },
    };
    return statusMap[status] || { label: status, class: 'badge-secondary', icon: '❓' };
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDateFromBlur = () => {
    setFilterDateFrom(tempDateFrom);
  };

  const handleDateToBlur = () => {
    setFilterDateTo(tempDateTo);
  };

  if (isLoading) {
    return <div className="loading">Cargando asistencias...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar asistencias</h3>
          <p>{error.response?.data?.detail || error.message || 'Error desconocido'}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => queryClient.invalidateQueries('attendances')}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Control de Asistencias</h2>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handleGroupAttendanceClick}>
            📋 Marcar por Grupo
          </button>
          <button className="btn btn-primary" onClick={handleCreateClick}>
            + Nueva Asistencia
          </button>
        </div>
      </div>

      <div className="card">
        <div className="filters-bar">
          <div className="autocomplete-wrapper">
            <StudentSearchSelect
              value={filterStudent}
              inputClassName="filter-input"
              placeholder="Buscar estudiante por nombre o documento..."
              onChange={(studentId) => setFilterStudent(studentId || '')}
            />
          </div>

          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los grupos</option>
            {groups?.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} ({group.code})
              </option>
            ))}
          </select>

          <div className="date-filter-wrapper">
            <label htmlFor="date-from" className="date-label">Fecha inicio:</label>
            <input
              id="date-from"
              type="date"
              value={tempDateFrom}
              onChange={(e) => setTempDateFrom(e.target.value)}
              onBlur={handleDateFromBlur}
              className="filter-date"
            />
          </div>

          <div className="date-filter-wrapper">
            <label htmlFor="date-to" className="date-label">Fecha fin:</label>
            <input
              id="date-to"
              type="date"
              value={tempDateTo}
              onChange={(e) => setTempDateTo(e.target.value)}
              onBlur={handleDateToBlur}
              className="filter-date"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha y Hora</th>
                <th>Estudiante</th>
                <th>Grupo</th>
                <th>Estado</th>
                <th>Método</th>
                <th>Marcado por</th>
                <th>Notas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {attendances && attendances.length > 0 ? (
                attendances.map((attendance) => {
                  const student = attendance.student;
                  const group = groups?.find(g => g.id === attendance.group_id);
                  const statusInfo = getStatusLabel(attendance.status);
                  
                  return (
                    <tr key={attendance.id}>
                      <td>{formatDateTime(attendance.attendance_date)}</td>
                      <td>
                        <strong>
                          {student?.full_name
                            || (student ? `${student.first_name} ${student.last_name}` : `ID: ${attendance.student_id}`)}
                        </strong>
                      </td>
                      <td>
                        {group ? (
                          <span className="group-badge">
                            {group.discipline?.icon || '⚽'} {group.name}
                          </span>
                        ) : (
                          `ID: ${attendance.group_id}`
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusInfo.class}`}>
                          {statusInfo.icon} {statusInfo.label}
                        </span>
                      </td>
                      <td>
                        <span className="method-badge">
                          {attendance.mark_method === 'qr_scan' ? '📱 QR' : '✋ Manual'}
                        </span>
                      </td>
                      <td>
                        {attendance.marked_by_user ? (
                          <div className="marked-by-info">
                            <strong>{attendance.marked_by_user.full_name}</strong>
                            <span className="user-role-badge">
                              {attendance.marked_by_user.role === 'teacher' ? '👨‍🏫 Docente' : 
                               attendance.marked_by_user.role === 'admin' ? '👤 Admin' : 
                               attendance.marked_by_user.role === 'superuser' ? '⚙️ Super' : 
                               '👤 Usuario'}
                            </span>
                          </div>
                        ) : (
                          `ID: ${attendance.marked_by_user_id}`
                        )}
                      </td>
                      <td>
                        <span className="notes-text">
                          {attendance.notes || '-'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="btn btn-secondary btn-sm" 
                          onClick={() => handleEditClick(attendance)}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>
                    No hay asistencias registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={editingAttendance ? 'Editar Asistencia' : 'Nueva Asistencia'}
        closeOnOverlayClick={false}
      >
        <AttendanceForm
          initialData={editingAttendance}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
          isLoading={createAttendanceMutation.isLoading || updateAttendanceMutation.isLoading}
        />
      </Modal>

      <Modal 
        isOpen={isGroupModalOpen} 
        onClose={() => setIsGroupModalOpen(false)} 
        title="Marcar Asistencias por Grupo"
        closeOnOverlayClick={false}
      >
        <GroupAttendanceForm
          onCancel={() => setIsGroupModalOpen(false)}
          onSuccess={() => {
            setIsGroupModalOpen(false);
            queryClient.invalidateQueries('attendances');
          }}
        />
      </Modal>
    </div>
  );
};

export default Attendances;
